import {
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ComponentProps,
} from 'react';
import type * as Y from 'yjs';
import Markdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  headingAnchor,
  parseFrontmatter,
  parseWikiLinkInner,
  titleFromPath,
} from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { vaultFileUrl } from '../navigation.js';
import { extractSection, mediaKind, wikiDisplayText } from './helpers.js';
import { resolveOnce } from './resolver.js';
import type { NavTarget } from './context.js';
import type { NoteEditorProps } from './index.jsx';

/** Reading mode: react-markdown + remark-gfm + a small remark transform that
 * turns [[wikilinks]] / ![[embeds]] into elements, with clickable links,
 * toggleable checkboxes, and depth-1 note transclusion. */

type HastElement = NonNullable<ExtraProps['node']>;

interface MdNode {
  type: string;
  value?: string;
  checked?: boolean | null;
  children?: MdNode[];
  data?: { hProperties?: Record<string, unknown> };
  position?: { start?: { offset?: number } };
  [key: string]: unknown;
}

const WIKI_RE = /(!?)\[\[([^\[\]\n]+)\]\]/g;

function splitWikilinks(text: string): MdNode[] {
  const out: MdNode[] = [];
  let last = 0;
  WIKI_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = WIKI_RE.exec(text))) {
    if (m.index > last) out.push({ type: 'text', value: text.slice(last, m.index) });
    out.push({
      type: 'link',
      url: '#',
      data: {
        hProperties: { 'data-wikilink': m[2], 'data-embed': m[1] === '!' ? '1' : '0' },
      },
      children: [],
    });
    last = m.index + m[0].length;
  }
  if (last === 0) return [{ type: 'text', value: text }];
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}

const TASK_MARKER = /^(\s*(?:>\s*)*(?:[-*+]|\d+[.)])\s+)\[([ xX])\]/;

/** Remark transform: wikilinks -> link nodes; each task list item stamped with
 * the exact source offset of its checkbox state char, taken from remark's OWN
 * mdast position (so render and toggle share one parser — a parallel scanner
 * would disagree on indented-code / HTML blocks and toggle the wrong line). */
function remarkWikilinks(body: string) {
  return (tree: unknown) => {
    const visit = (node: MdNode) => {
      if (
        node.type === 'listItem' &&
        typeof node.checked === 'boolean' &&
        node.position?.start?.offset != null
      ) {
        const start = node.position.start.offset;
        const m = TASK_MARKER.exec(body.slice(start, start + 96));
        if (m) {
          node.data = node.data ?? {};
          node.data.hProperties = {
            ...(node.data.hProperties ?? {}),
            // +m[1].length steps over the list marker, +1 over the '['.
            'data-taskoff': String(start + m[1].length + 1),
          };
        }
      }
      if (node.type === 'code' || node.type === 'inlineCode' || !node.children) return;
      const next: MdNode[] = [];
      for (const child of node.children) {
        if (child.type === 'text' && child.value && child.value.includes('[[')) {
          next.push(...splitWikilinks(child.value));
        } else {
          visit(child);
          next.push(child);
        }
      }
      node.children = next;
    };
    visit(tree as MdNode);
  };
}

function hastText(node: HastElement | undefined): string {
  if (!node) return '';
  let out = '';
  const walk = (n: { type: string; value?: string; children?: unknown[] }) => {
    if (n.type === 'text' && n.value) out += n.value;
    for (const c of n.children ?? []) walk(c as { type: string; value?: string });
  };
  walk(node as { type: string; children?: unknown[] });
  return out;
}

function propStr(props: object, key: string): string | undefined {
  const v = (props as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : undefined;
}

const EXTERNAL_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

// ---------------------------------------------------------------------------

interface ReadingMarkdownProps {
  md: string;
  sourceId: string;
  onNavigate: (t: NavTarget) => void;
  depth: number;
  /** ytext of the note the offsets map into; omit to disable checkbox toggling. */
  ytext?: Y.Text;
}

function WikiEmbed({
  inner,
  sourceId,
  onNavigate,
  depth,
}: {
  inner: string;
  sourceId: string;
  onNavigate: (t: NavTarget) => void;
  depth: number;
}) {
  const w = parseWikiLinkInner(inner, true, 0, 0, `![[${inner}]]`);
  const res = resolveOnce(w.target, sourceId);
  const nav: NavTarget = { target: w.target, heading: w.heading, blockId: w.blockId };
  if (res.id) nav.id = res.id;
  if (!res.id || !res.path) {
    return (
      <span className="lp-link lp-link-unresolved" onClick={() => onNavigate(nav)}>
        {wikiDisplayText(w)}
      </span>
    );
  }
  const kind = mediaKind(res.path);
  const url = vaultFileUrl(res.path);
  if (kind === 'image') return <img className="reading-media" src={url} alt={w.alias ?? w.target} />;
  if (kind === 'audio') return <audio className="reading-media" controls preload="metadata" src={url} />;
  if (kind === 'video') return <video className="reading-media" controls playsInline preload="metadata" src={url} />;
  const entry = vault.getEntry(res.id);
  if (entry?.kind !== 'note' || depth >= 1) {
    return (
      <span className="lp-link" onClick={() => onNavigate(nav)}>
        {wikiDisplayText(w)}
      </span>
    );
  }
  const md = vault.noteContent(res.id);
  const section = extractSection(md, { heading: w.heading, blockId: w.blockId });
  return (
    <span className="reading-embed">
      <span className="reading-embed-title" onClick={() => onNavigate(nav)}>
        {w.alias ?? wikiDisplayText({ ...w, target: titleFromPath(res.path) })}
      </span>
      <ReadingMarkdown md={section} sourceId={res.id} onNavigate={onNavigate} depth={depth + 1} />
    </span>
  );
}

export function ReadingMarkdown({ md, sourceId, onNavigate, depth, ytext }: ReadingMarkdownProps) {
  const { bodyStart } = parseFrontmatter(md);
  const body = md.slice(bodyStart);
  const remarkWikilinksPlugin = useMemo(() => () => remarkWikilinks(body), [body]);

  const components = useMemo<Components>(() => {
    const heading =
      (Tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') =>
      ({ node, children, ...rest }: ComponentProps<typeof Tag> & ExtraProps) => (
        <Tag {...rest} data-anchor={headingAnchor(hastText(node)).toLowerCase()}>
          {children}
        </Tag>
      );

    return {
      h1: heading('h1'),
      h2: heading('h2'),
      h3: heading('h3'),
      h4: heading('h4'),
      h5: heading('h5'),
      h6: heading('h6'),

      a: (props) => {
        const { node, children, href, ...rest } = props;
        const inner =
          propStr(props, 'data-wikilink') ??
          (typeof node?.properties?.['data-wikilink'] === 'string'
            ? (node.properties['data-wikilink'] as string)
            : undefined) ??
          (typeof node?.properties?.dataWikilink === 'string'
            ? (node.properties.dataWikilink as string)
            : undefined);
        if (inner != null) {
          const embed =
            propStr(props, 'data-embed') === '1' ||
            node?.properties?.['data-embed'] === '1' ||
            node?.properties?.dataEmbed === '1';
          if (embed) {
            return (
              <WikiEmbed inner={inner} sourceId={sourceId} onNavigate={onNavigate} depth={depth} />
            );
          }
          const w = parseWikiLinkInner(inner, false, 0, 0, `[[${inner}]]`);
          const res = resolveOnce(w.target, sourceId);
          const nav: NavTarget = { target: w.target, heading: w.heading, blockId: w.blockId };
          if (res.id) nav.id = res.id;
          return (
            <span
              role="link"
              tabIndex={0}
              className={res.id ? 'lp-link' : 'lp-link lp-link-unresolved'}
              onClick={() => onNavigate(nav)}
            >
              {wikiDisplayText(w)}
            </span>
          );
        }
        const url = href ?? '';
        if (EXTERNAL_RE.test(url) || url.startsWith('//')) {
          return (
            <a {...rest} href={url} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          );
        }
        if (url.startsWith('#')) {
          return (
            <a
              {...rest}
              href={url}
              onClick={(e) => {
                e.preventDefault();
                onNavigate({ heading: decodeURIComponent(url.slice(1)) });
              }}
            >
              {children}
            </a>
          );
        }
        return (
          <a
            {...rest}
            href={url}
            onClick={(e) => {
              e.preventDefault();
              onNavigate({ target: decodeURIComponent(url) });
            }}
          >
            {children}
          </a>
        );
      },

      img: ({ node: _node, src, alt, ...rest }) => {
        const url = typeof src === 'string' ? src : '';
        if (url && !EXTERNAL_RE.test(url) && !url.startsWith('//') && !url.startsWith('/')) {
          const res = resolveOnce(decodeURIComponent(url), sourceId);
          if (res.path) {
            return <img {...rest} className="reading-media" src={vaultFileUrl(res.path)} alt={alt ?? ''} />;
          }
        }
        return <img {...rest} className="reading-media" src={url} alt={alt ?? ''} />;
      },

      li: (props) => {
        const { node, children, ...rest } = props;
        const offRaw =
          propStr(props, 'data-taskoff') ??
          (typeof node?.properties?.['data-taskoff'] === 'string'
            ? (node.properties['data-taskoff'] as string)
            : undefined) ??
          (typeof node?.properties?.dataTaskoff === 'string'
            ? (node.properties.dataTaskoff as string)
            : undefined);
        if (offRaw == null || !ytext) return <li {...rest}>{children}</li>;
        // data-taskoff is relative to `body`; the Y.Text includes frontmatter.
        const at = Number(offRaw) + bodyStart;
        return (
          <li
            {...rest}
            onClickCapture={(e) => {
              const t = e.target as HTMLElement;
              if (t.tagName !== 'INPUT') return;
              if (t.closest('li') !== e.currentTarget) return;
              e.preventDefault();
              e.stopPropagation();
              const cur = ytext.toString().charAt(at);
              if (cur !== ' ' && cur.toLowerCase() !== 'x') return;
              const doc = ytext.doc;
              const apply = () => {
                ytext.delete(at, 1);
                ytext.insert(at, cur === ' ' ? 'x' : ' ');
              };
              if (doc) doc.transact(apply);
              else apply();
            }}
          >
            {children}
          </li>
        );
      },

      input: ({ node: _node, disabled: _disabled, ...rest }) => {
        if (rest.type === 'checkbox') {
          return <input {...rest} className="task-checkbox" readOnly onChange={() => {}} />;
        }
        return <input {...rest} />;
      },
    };
  }, [sourceId, onNavigate, depth, ytext, bodyStart]);

  return (
    <Markdown remarkPlugins={[remarkGfm, remarkWikilinksPlugin]} components={components}>
      {body}
    </Markdown>
  );
}

// ---------------------------------------------------------------------------

function useYTextString(ytext: Y.Text): string {
  return useSyncExternalStore(
    (onChange) => {
      const h = () => onChange();
      ytext.observe(h);
      return () => ytext.unobserve(h);
    },
    () => ytext.toString(),
  );
}

export function ReadingView(props: NoteEditorProps) {
  const content = useYTextString(props.ytext);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Scroll to the anchor only ONCE per target, when it first renders — not on
  // every content change (a checkbox toggle or remote edit would otherwise
  // yank the viewport back to the heading each time).
  const scrolledFor = useRef<string | null>(null);

  useEffect(() => {
    scrolledFor.current = null; // reset when the navigation target changes
  }, [props.noteId, props.scrollToHeading, props.scrollToBlock]);

  useEffect(() => {
    if (!props.scrollToHeading && !props.scrollToBlock) return;
    const key = `${props.noteId}|${props.scrollToHeading ?? ''}|${props.scrollToBlock ?? ''}`;
    if (scrolledFor.current === key) return;
    const root = rootRef.current;
    if (!root) return;
    let target: Element | null = null;
    if (props.scrollToHeading) {
      const want = headingAnchor(props.scrollToHeading).toLowerCase();
      target = root.querySelector(`[data-anchor="${CSS.escape(want)}"]`);
    } else if (props.scrollToBlock) {
      const marker = `^${props.scrollToBlock}`;
      for (const el of root.querySelectorAll('p, li')) {
        if (el.textContent?.includes(marker)) {
          target = el;
          break;
        }
      }
    }
    if (target) {
      scrolledFor.current = key; // found it — don't re-scroll on later edits
      target.scrollIntoView({ block: 'start' });
      target.classList.add('flash');
      const t = window.setTimeout(() => target?.classList.remove('flash'), 1600);
      return () => window.clearTimeout(t);
    }
    return;
  }, [props.noteId, props.scrollToHeading, props.scrollToBlock, content]);

  return (
    <div className="reading-view" ref={rootRef}>
      <div className="reading-body">
        <ReadingMarkdown
          md={content}
          sourceId={props.noteId}
          onNavigate={props.onNavigate}
          depth={0}
          ytext={props.ytext}
        />
      </div>
    </div>
  );
}
